import React, { useEffect } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import SparkleIcon from "Components/common/SparkleIcon";

/*
 * What the drawer shows before a conversation exists. The thread area is empty
 * at that point, and an empty panel reads as broken rather than ready -- so it
 * offers a way in: things worth asking about THIS page, and the last chat to
 * pick back up.
 */

// Openers worth suggesting per section. Page-specific on purpose: the generic
// prompt bank teaches the product, but someone standing on Inbox wants inbox
// questions, not "find fintech companies".
const STARTERS = {
  inbox: [
    "Who needs a reply from me?",
    "Why are replies down?",
    "Draft a reply to the latest positive response"
  ],
  tasks: [
    "What should I do first today?",
    "Which tasks are blocking sequences?",
    "Clear tasks for people who replied"
  ],
  reports: [
    "How did this week compare?",
    "What's my biggest lever right now?",
    "Which sequence should I copy?"
  ],
  contacts: [
    "Who in this list is most likely to reply?",
    "Build me a similar list",
    "Which contacts are missing data?"
  ],
  contacts_detail: [
    "What do I know about them?",
    "Draft an opener",
    "What is the next step?"
  ],
  settings: [
    "Is my setup healthy?",
    "How many credits do I have left?",
    "Why should I add another inbox?"
  ],
  calls: [
    "Who should I call today?",
    "When do my calls connect best?",
    "Summarize yesterday's calls"
  ],
  list_builder: [
    "Find prospects for my product",
    "Find companies like my best customer",
    "Tighten this to decision makers"
  ],
  sequences: [
    "Which sequence is performing best?",
    "What should I fix first?",
    "Which sequences are paused or draft?"
  ],
  sequences_detail: [
    "How is this sequence doing?",
    "Which step is losing people?",
    "Rewrite the first email"
  ],
  accounts: ["Which accounts are worth working?", "Who has no contacts yet?"],
  dashboard: ["How am I doing this week?", "What needs attention today?"],
  templates: ["Which template gets replies?", "Write me a new one"],
  deep_research: ["Turn this into an opener", "Build a sequence from this"]
};

const DEFAULT_STARTERS = [
  "What can you do here?",
  "What needs my attention?",
  "Summarise this page"
];

const DrawerStartup = observer(({ surface }) => {
  // The recent list is what makes "pick up where you left off" possible, and
  // the drawer may never have opened its history panel.
  useEffect(() => {
    if (!copilotStore.conversations.length) copilotStore.loadConversations();
  }, []);

  const starters = (STARTERS[surface] || DEFAULT_STARTERS).slice(0, 3);
  const recent = copilotStore.conversations.length ? copilotStore.conversations[0] : null;

  return (
    <div className="cdStartup">
      <SparkleIcon className="cdStartup__mark" />
      <div className="cdStartup__title">What&apos;s on your mind?</div>
      <div className="cdStartup__sub">
        I can read this page, answer questions about it, and act on it for you.
      </div>

      <div className="cdStartup__label">Try asking</div>
      <div className="cdStartup__chips">
        {starters.map(text => (
          <button
            type="button"
            key={text}
            className="cdStartup__chip"
            onClick={() => copilotStore.sendUserMessage(text)}
          >
            {text}
          </button>
        ))}
      </div>

      {recent && (
        <>
          <div className="cdStartup__label">Pick up where you left off</div>
          <button
            type="button"
            className="cdStartup__resume"
            onClick={() => copilotStore.loadConversation(recent.id)}
          >
            <i className="material-icons-outlined">history</i>
            <span>{recent.title || "Last conversation"}</span>
          </button>
        </>
      )}
    </div>
  );
});

export default DrawerStartup;
